import React, { useEffect, useRef, useState } from "react";
import {
  MDBBadge,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBIcon,
  MDBSpinner,
} from "mdb-react-ui-kit";
import { toast } from "react-toastify";

import {
  getGoogleSearchUrl,
  searchRorOrganizations,
} from "../../../helpers/ror";

const ROR_RESULT_LIMIT = 3;
const SEARCH_DEBOUNCE_MS = 400;
const BORDER_COLORS = ["primary", "warning", "info"];

const RorSearchResult = ({ option, onSelectRor, borderColor }) => {
  const location = [option?.city, option?.country].filter(Boolean).join(", ");
  const aliasText = option.aliases?.join(", ");

  return (
    <div
      className={`d-flex align-items-center gap-2 border-start border-3 border-${borderColor} ps-2 py-1`}
    >
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="fw-semibold" style={{ fontSize: "0.875rem", lineHeight: 1.25 }}>
          {option.ror_name}
          {location && (
            <span className="fw-normal text-muted">{`, ${location}`}</span>
          )}
        </div>
        {aliasText && (
          <div
            className="text-muted text-truncate"
            style={{ fontSize: "0.75rem", lineHeight: 1.2 }}
            title={aliasText}
          >
            {aliasText}
          </div>
        )}
        <a
          href={option.ror_id}
          target="_blank"
          rel="noreferrer"
          className="text-decoration-none d-inline-block text-truncate w-100"
          style={{ fontSize: "0.75rem", lineHeight: 1.2 }}
        >
          {option.ror_id}
        </a>
      </div>
      <MDBBtn
        size="sm"
        color={borderColor}
        className="px-2 py-1"
        onClick={() => onSelectRor(option)}
        aria-label={`Select ${option.ror_name}`}
        title="Select"
      >
        <MDBIcon fas icon="check" />
      </MDBBtn>
    </div>
  );
};

const TaggedAffiliation = ({ affiliation, taggedSubstrings }) => {
  const textClassName = "lh-sm";

  if (!taggedSubstrings.length) {
    return <span className={textClassName}>{affiliation}</span>;
  }

  const parts = [];
  let remaining = affiliation;

  const sortedTags = [...taggedSubstrings].sort(
    (a, b) => affiliation.indexOf(a) - affiliation.indexOf(b)
  );

  sortedTags.forEach((tag) => {
    const index = remaining.indexOf(tag);
    if (index === -1) {
      return;
    }

    if (index > 0) {
      parts.push({ text: remaining.slice(0, index), tagged: false });
    }

    parts.push({ text: tag, tagged: true });
    remaining = remaining.slice(index + tag.length);
  });

  if (remaining) {
    parts.push({ text: remaining, tagged: false });
  }

  return (
    <span className={textClassName}>
      {parts.map((part, index) =>
        part.tagged ? (
          <mark key={`${part.text}-${index}`} className="bg-warning px-1">
            {part.text}
          </mark>
        ) : (
          <span key={`${part.text}-${index}`}>{part.text}</span>
        )
      )}
    </span>
  );
};

const buildSearchQueryFromTags = (tags, affiliationText) => {
  if (!tags.length) {
    return affiliationText;
  }

  return [...tags]
    .sort((a, b) => affiliationText.indexOf(a) - affiliationText.indexOf(b))
    .join(" ");
};

const iconButtonClass = "px-2 py-1";

const SEARCH_QUERY_MAX_HEIGHT = 72;

export const TagRorCard = ({ task, onTagRor }) => {
  const affiliationRef = useRef(null);
  const searchRef = useRef(null);
  const affiliation = task.data?.affiliation || "";

  const [taggedSubstrings, setTaggedSubstrings] = useState([]);
  const [searchQuery, setSearchQuery] = useState(affiliation);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    setTaggedSubstrings([]);
    setSearchQuery(affiliation);
    setSearchResults([]);
    setHasSearched(false);
  }, [task.id, affiliation]);

  useEffect(() => {
    const field = searchRef.current;
    if (!field) {
      return;
    }

    const fitSearchField = () => {
      field.style.height = "auto";
      const nextHeight = Math.min(field.scrollHeight, SEARCH_QUERY_MAX_HEIGHT);
      field.style.height = `${nextHeight}px`;
      field.style.overflowY =
        field.scrollHeight > SEARCH_QUERY_MAX_HEIGHT ? "auto" : "hidden";
    };

    fitSearchField();
    window.addEventListener("resize", fitSearchField);
    return () => window.removeEventListener("resize", fitSearchField);
  }, [searchQuery]);

  useEffect(() => {
    const query = searchQuery.trim();

    if (!query) {
      setSearchResults([]);
      setHasSearched(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const timeoutId = setTimeout(async () => {
      try {
        const results = await searchRorOrganizations(query, ROR_RESULT_LIMIT);
        setSearchResults(results);
        setHasSearched(true);
      } catch (error) {
        toast("Error searching ROR records", { type: "error" });
        setSearchResults([]);
        setHasSearched(true);
      } finally {
        setIsSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const buildOutput = (selection, rorMissing) => {
    const output = {
      search_query: searchQuery.trim() || affiliation,
      ror_id: selection?.ror_id ?? null,
    };

    if (selection?.ror_name) {
      output.ror_name = selection.ror_name;
    }

    if (taggedSubstrings.length > 0) {
      output.tagged_substrings = taggedSubstrings;
    }

    if (output.ror_id === null && rorMissing) {
      output.ror_missing = rorMissing;
    }

    return output;
  };

  const handleTagSelection = () => {
    const selection = window.getSelection();
    const container = affiliationRef.current;

    if (
      !selection ||
      selection.isCollapsed ||
      !container ||
      !container.contains(selection.anchorNode) ||
      !container.contains(selection.focusNode)
    ) {
      toast("Select text in the affiliation first, then click the tag button", {
        type: "warning",
      });
      return;
    }

    const selectedText = selection.toString().trim();

    if (!selectedText) {
      toast("Select text in the affiliation first, then click the tag button", {
        type: "warning",
      });
      return;
    }

    if (!affiliation.includes(selectedText)) {
      toast("Selected text must be part of the affiliation", { type: "error" });
      return;
    }

    if (taggedSubstrings.includes(selectedText)) {
      toast("This substring is already tagged", { type: "info" });
      return;
    }

    setTaggedSubstrings((current) => {
      const newTags = [...current, selectedText];
      setSearchQuery(buildSearchQueryFromTags(newTags, affiliation));
      return newTags;
    });
    selection.removeAllRanges();
  };

  const handleRemoveTag = (tag) => {
    setTaggedSubstrings((current) => {
      const newTags = current.filter((item) => item !== tag);
      setSearchQuery(buildSearchQueryFromTags(newTags, affiliation));
      return newTags;
    });
  };

  const handleGoogleSearch = () => {
    const query = searchQuery.trim() || affiliation;
    if (!query) {
      toast("Search query cannot be empty", { type: "error" });
      return;
    }

    window.open(getGoogleSearchUrl(query), "_blank", "noopener,noreferrer");
  };

  const handleSelectRor = (option) => {
    onTagRor(
      buildOutput({
        ror_id: option.ror_id,
        ror_name: option.ror_name,
      })
    );
  };

  const handleNoMatch = (rorMissing) => {
    onTagRor(buildOutput(null, rorMissing));
  };

  return (
    <MDBCard className="mb-2 text-start">
      <MDBCardBody className="p-2">
        <div className="d-flex align-items-start gap-1">
          <div
            id="tag-ror-affiliation"
            ref={affiliationRef}
            className="form-control form-control-sm py-1 flex-grow-1"
            aria-label="Affiliation text"
            style={{
              height: "auto",
              minHeight: "unset",
              cursor: "text",
              whiteSpace: "normal",
              userSelect: "text",
              fontSize: "0.875rem",
              lineHeight: 1.3,
            }}
          >
            <TaggedAffiliation
              affiliation={affiliation}
              taggedSubstrings={taggedSubstrings}
            />
          </div>
          <MDBBtn
            size="sm"
            color="secondary"
            className={iconButtonClass}
            onClick={handleTagSelection}
            aria-label="Tag selected text"
            title="Tag selected text"
          >
            <MDBIcon fas icon="tag" />
          </MDBBtn>
        </div>

        {taggedSubstrings.length > 0 && (
          <div className="d-flex flex-wrap align-items-center gap-1 mt-1">
            {taggedSubstrings.map((tag) => (
              <MDBBadge
                key={tag}
                color="warning"
                className="d-inline-flex align-items-center"
                pill
              >
                {tag}
                <button
                  type="button"
                  className="btn-close btn-close-white ms-1"
                  aria-label={`Remove tag ${tag}`}
                  onClick={() => handleRemoveTag(tag)}
                  style={{ fontSize: "0.5rem" }}
                />
              </MDBBadge>
            ))}
          </div>
        )}

        <div className="input-group input-group-sm mt-1">
          <textarea
            id="tag-ror-search-query"
            ref={searchRef}
            className="form-control"
            rows={1}
            value={searchQuery}
            aria-label="Search query"
            placeholder="Search query"
            onChange={(event) => setSearchQuery(event.target.value)}
            style={{ resize: "none", lineHeight: 1.3 }}
          />
          <MDBBtn
            color="light"
            className={`${iconButtonClass} border`}
            onClick={handleGoogleSearch}
            aria-label="Search Google"
            title="Search Google"
          >
            <MDBIcon fab icon="google" />
          </MDBBtn>
        </div>

        {searchQuery.trim() && (isSearching || !hasSearched) && (
          <div
            className="d-flex align-items-center gap-2 mt-2 text-muted"
            style={{ fontSize: "0.8rem" }}
            role="status"
          >
            <MDBSpinner size="sm">
              <span className="visually-hidden">Searching ROR</span>
            </MDBSpinner>
            Searching ROR
          </div>
        )}

        {hasSearched && !isSearching && (
          <div className="d-flex flex-column gap-1 mt-2">
            {searchResults.length === 0 && (
              <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                No ROR records found
              </div>
            )}
            {searchResults.map((option, index) => (
              <RorSearchResult
                key={option.ror_id}
                option={option}
                onSelectRor={handleSelectRor}
                borderColor={BORDER_COLORS[index % BORDER_COLORS.length]}
              />
            ))}
            <div className="d-flex flex-wrap gap-1">
              <MDBBtn
                size="sm"
                color="danger"
                className={iconButtonClass}
                onClick={() => handleNoMatch("invalid")}
              >
                Invalid
              </MDBBtn>
              <MDBBtn
                size="sm"
                color="secondary"
                className={iconButtonClass}
                onClick={() => handleNoMatch("notinror")}
              >
                Not in ROR
              </MDBBtn>
            </div>
          </div>
        )}
      </MDBCardBody>
    </MDBCard>
  );
};
